import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '../../../fi-dialogs/fi-dialogs-content-base';
import {
    IAddChecklistDialogResult,
    IAddChecklistTemplateDialogResult,
    IChecklistCreationTypeOption,
} from '../../checklist';
import { IChecklistV2, ILightWeightChecklistV2 } from '@fi-shared/checklist/checklist-v2';
import {
    FiChecklistHelperService,
    IChecklistCheckpointPlaceholderLabelData,
} from '@fi-shared/checklist/fi-checklist-helper.service';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { IFiDialogOptions } from '@fi-shared/fi-dialogs/fi-dialogs';
import { ObjectTypes } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ChecklistCreationTypes } from '../../checklist-enum';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

interface IChecklistAddChecklistTemplateDialogData {
    auditMode: boolean;
}

@Component({
    selector: 'fi-add-checklist-template-dialog',
    templateUrl: './add-checklist-template-dialog.component.html',
    styleUrl: './add-checklist-template-dialog.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-add-checklist-template-dialog' },
    standalone: false,
})
export class FiChecklistAddChecklistTemplateDialogComponent
    extends FiDialogsContentBase<IChecklistAddChecklistTemplateDialogData, IAddChecklistTemplateDialogResult>
    implements OnInit
{
    checklist: IChecklistV2;
    checklistPlaceholderLabelData: IChecklistCheckpointPlaceholderLabelData;
    protected currentChecklistCreationType: ChecklistCreationTypes;
    protected checklistCreationTypeOptions: IChecklistCreationTypeOption[] = [];
    protected selectedTemplateId: number;
    private fiValuesComparison: FiValuesComparison<IChecklistV2>;

    protected readonly ObjectTypes = ObjectTypes;

    protected lightWeightChecklistTemplates$ = new BehaviorSubject<ILightWeightChecklistV2[]>(undefined);

    private get auditMode(): boolean {
        return this.data.auditMode;
    }

    ngOnInit() {
        this.setServices();
        this.setChecklistCreationTypeOptions();
        this.setInitialChecklistCommonData();
        this.setChecklistLabels();
        this.setCurrentChecklistCreationType();
        this.fiValuesComparison = new FiValuesComparison(() => this.checklist);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        const dialogData: IFiDialogOptions<null, null> =
            this.fiChecklistHelperService.getChecklistDiscardChangesDialogData(
                this.checklist.Id,
                ObjectTypes.Checklist2,
            );
        const conCloseCondition: boolean = !isDefined(this.selectedTemplateId) && !this.fiValuesComparison.isChanged();

        return this.fiDialogsService.getCanCloseConfirmation(conCloseCondition, dialogData);
    }

    private setChecklistCreationTypeOptions() {
        this.checklistCreationTypeOptions = [
            {
                type: ChecklistCreationTypes.Manual,
                label: this.appTextService.get('checklist.checklist_template_create_manually'),
                description: this.appTextService.get('checklist.checklist_template_create_manually_description'),
            },
            {
                type: ChecklistCreationTypes.UploadFromExcel,
                label: this.appTextService.get('common.upload_from_excel'),
                description: this.appTextService.get('checklist.checklist_upload_from_excel_description', {
                    '{0}': this.appTextService.get('common.template').toLowerCase(),
                }),
            },
        ];

        if (this.fiCustomerDataService.customerData.HasTemplateCustomer) {
            this.checklistCreationTypeOptions.splice(1, 0, {
                type: ChecklistCreationTypes.Template,
                label: this.appTextService.get('checklist.checklist_template_create_using_template'),
                description: this.appTextService.get('checklist.checklist_template_create_using_template_description'),
            });
        }
    }

    private setCurrentChecklistCreationType() {
        this.currentChecklistCreationType = ChecklistCreationTypes.Manual;
        this.onCurrentChecklistCreationTypeChange();
    }

    protected showChecklistCreationTypeOptions() {
        return true;
        // todo after releaseing FI-6636 Excel import - Checklists 2.0, Templates and Audits
        // remove related markup and css
        //return this.fiCustomerDataService.customerData.HasTemplateCustomer;
    }

    private setInitialChecklistCommonData() {
        this.checklist = this.fiChecklistHelperService.getChecklistInstanceV2();
    }

    private setChecklistLabels() {
        this.checklistPlaceholderLabelData = this.fiChecklistHelperService.checklistTemplatePlaceholderLabelData;
    }

    onCurrentChecklistCreationTypeChange() {
        this.selectedTemplateId = undefined;
        this.setInitialChecklistCommonData();
        if (
            this.currentChecklistCreationType === ChecklistCreationTypes.Template &&
            !this.lightWeightChecklistTemplates$.value
        ) {
            // load lightweight templates on 1st switch to ChecklistCreationTypes.Template for performance purposes
            this.fiChecklist2TemplateService.getChecklistTemplateSamples(this.auditMode).then((items) => {
                this.lightWeightChecklistTemplates$.next(items);
            });
        }
    }

    getNewChecklistHeader(): string {
        // todo use something like 'create new {0}' where {0} depends from audit model ,
        return this.appTextService.get('checklist.create_new_template');
    }

    isYesButtonDisabled(): boolean {
        if (this.isChecklistCreationTypeSelected(ChecklistCreationTypes.Template)) {
            return !this.selectedTemplateId;
        }

        if (this.isChecklistCreationTypeSelected(ChecklistCreationTypes.UploadFromExcel)) {
            return !this.checklist.Names.find((item) => item.Value)?.Value;
        }

        return !this.checklist.Names.find((name: IContentLanguageLabelModel) => {
            return name.ContentLanguageId === this.getUserPreferredContentLanguage();
        })?.Value;
    }

    getUserPreferredContentLanguage(): number {
        return this.fiUserDataService.getContentLanguage();
    }

    isChecklistCreationTypeSelected(creationType: ChecklistCreationTypes): boolean {
        return this.currentChecklistCreationType === creationType;
    }

    protected onChecklistImported($event: IChecklistV2) {
        this.checklist = $event;
        if (!this.checklist) {
            this.setInitialChecklistCommonData();
        }
    }

    getResult(): IAddChecklistDialogResult {
        return {
            type: this.currentChecklistCreationType,
            checklist: this.checklist,
            templateId: this.selectedTemplateId,
        };
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly ChecklistCreationTypes = ChecklistCreationTypes;

    private fiChecklistHelperService: FiChecklistHelperService;
    private fiUserDataService: FiUserDataService;
    private appTextService: AppTextService;
    private fiDialogsService: FiDialogsService;
    private fiChecklist2TemplateService: FiChecklist2TemplateService;
    private fiCustomerDataService: FiCustomerDataService;
    private cdr: ChangeDetectorRef;

    private setServices() {
        this.fiChecklistHelperService = this.injector.get(FiChecklistHelperService);
        this.fiUserDataService = this.injector.get(FiUserDataService);
        this.fiCustomerDataService = this.injector.get(FiCustomerDataService);
        this.appTextService = this.injector.get(AppTextService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.fiChecklist2TemplateService = this.injector.get(FiChecklist2TemplateService);
        this.cdr = this.injector.get(ChangeDetectorRef);
    }
}
